// The Altar: offer up to five storage creatures for a chance at one ascension
// or star item. The creatures are gone either way. Reference: the Offering
// (pokeidle 05-endgame-bosses-pvp-guilds.md §2), where each creature is one
// fifth of a wheel. Here a creature's slice grows with its grade and level,
// so a better offering pays more often; the rest of the wheel is empty.
//
// Each creature paints its slice with items, split evenly over its elements: an
// element in ALTAR_GIFT's 'ascension' list paints that element's ascension stone,
// one in the 'star' list a star item. The star item is the trainer's, not the
// creature's: the item of the highest region whose star-item hunts the trainer
// has opened (altarStarItem), so a cheap creature from a later region never pays
// an item its trainer can't find yet (audit 2026-10-20: grade-C Yomi creatures
// paid about 15 Dragon Pearls a week from trainer level 20). So
//   slice(c)       = base + perGrade[grade] + perLevel × min(level, levelCap)
//   P(any item)    = sum of slices (at most 5 × the largest slice)
//   P(item X)      = sum over creatures of slice(c) × (c's elements painting X) / (c's elements)
// The items are the tradeable drops, so the weekly limit and the slices are
// tuned in the sim (sim/targets.ts altarShare bands) to stay a small extra on
// top of what hunting finds. No diamonds, no Auras (never offered).
import { SPECIES } from './species.ts';
import { elementStone, STAR_ITEM } from './items.ts';
import { REGIONS, starHuntLevel } from './grounds.ts';
import { ratingOf } from './rating.ts';
import { gradeOf, type Grade } from './grade.ts';
import { weekOf } from './recipes.ts';
import { isKept, keptIds } from './automation.ts';
import type { Element } from './elements.ts';
import type { CreatureDTO, TrainerDTO } from './protocol.ts';

export const ALTAR = {
  /** Creatures per offering. */
  slots: 5,
  /** Offerings per trainer per UTC week (Monday 00:00, the weekly quests' reset). */
  weekly: 30,
  /** Trainer level that opens the Altar. */
  minLevel: 20,
  /** A creature's slice of the wheel: base + per grade + per level (levels past levelCap add nothing). */
  base: 0.06,
  perGrade: { C: 0, B: 0.02, A: 0.05, S: 0.08, 'S+': 0.1 } as Record<Grade, number>,
  perLevel: 0.0002,
  levelCap: 200,
} as const;

/** Which item each element paints. */
export const ALTAR_GIFT: Record<'ascension' | 'star', readonly Element[]> = {
  ascension: ['grove', 'tide', 'frost', 'light', 'shade'],
  star: ['flame', 'storm', 'stone'],
};

/** A creature's slice of the wheel (chance it adds). */
export function altarSlice(c: Pick<CreatureDTO, 'species' | 'level' | 'rolls'>): number {
  const g = gradeOf(ratingOf(c.rolls, c.species));
  return ALTAR.base + ALTAR.perGrade[g] + ALTAR.perLevel * Math.min(Math.max(0, c.level), ALTAR.levelCap);
}

export interface AltarOdds {
  /** Chance of any item, 0..1. */
  chance: number;
  /** Chance of each item, largest first; they add up to `chance`. */
  items: { item: string; p: number }[];
}

/** The star item the Altar pays a trainer of this level: that of the highest region whose star-item hunts are open to them (Runes of Ascent before any). */
export function altarStarItem(trainerLevel: number): string {
  let item = STAR_ITEM.midgard;
  for (const r of REGIONS) if (trainerLevel >= starHuntLevel(r.id)) item = STAR_ITEM[r.id];
  return item;
}

/** The wheel for these creatures (up to ALTAR.slots; extras are ignored), offered by a trainer of this level. */
export function altarOdds(cs: readonly Pick<CreatureDTO, 'species' | 'level' | 'rolls'>[], trainerLevel: number): AltarOdds {
  const star = altarStarItem(trainerLevel);
  const by = new Map<string, number>();
  let chance = 0;
  for (const c of cs.slice(0, ALTAR.slots)) {
    const sp = SPECIES[c.species];
    if (!sp) continue;
    const slice = altarSlice(c);
    chance += slice;
    const els = sp.elements;
    for (const el of els) {
      const item = ALTAR_GIFT.star.includes(el) ? star : elementStone(el);
      by.set(item, (by.get(item) ?? 0) + slice / els.length);
    }
  }
  return { chance: Math.min(1, chance), items: [...by].map(([item, p]) => ({ item, p })).sort((a, b) => b.p - a.p) };
}

/** Spin: `r` in [0, 1). The item won, or null for an empty slot. */
export function altarDraw(odds: AltarOdds, r: number): string | null {
  let acc = 0;
  for (const x of odds.items) { acc += x.p; if (r < acc) return x.item; }
  return null;
}

type Holder = Pick<TrainerDTO, 'altar'>;
/** Offerings made this UTC week. */
export const altarUsed = (t: Holder, now: number) => (t.altar?.week === weekOf(now) ? t.altar.n : 0);
export const altarLeft = (t: Holder, now: number) => Math.max(0, ALTAR.weekly - altarUsed(t, now));
/** Count one offering this week. */
export function recordAltar(t: Holder, now: number) {
  const week = weekOf(now);
  t.altar = { week, n: (t.altar?.week === week ? t.altar.n : 0) + 1 };
}

/** May go on the Altar: never the team, a favorite, a listing, an Aura, a shrine post, an XP Charm holder or the starter. */
export function offerable(t: TrainerDTO, creatures: Iterable<CreatureDTO>): CreatureDTO[] {
  const keep = keptIds(t);
  const out: CreatureDTO[] = [];
  for (const c of creatures) if (!isKept(keep, c)) out.push(c);
  return out;
}

/** The worst offerable creatures: lowest grade score first, then lowest level, then newest. */
export function worstOfferable(t: TrainerDTO, creatures: Iterable<CreatureDTO>, n: number = ALTAR.slots): CreatureDTO[] {
  return offerable(t, creatures)
    .map((c) => ({ c, s: ratingOf(c.rolls, c.species) }))
    .sort((a, b) => a.s - b.s || a.c.level - b.c.level || b.c.caughtAt - a.c.caughtAt)
    .slice(0, n).map((x) => x.c);
}
